package org.teddapp.project.ui.projects

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Organization
import com.tedd.db.Project
import com.tedd.db.Skill
import com.tedd.db.ProjectStatus
import com.tedd.db.Task
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.TeddRepositories

/**
 * État et actions de la section Projets, indépendants de la mise en page.
 */
@Stable
internal class ProjectsModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
) {
    var organizations by mutableStateOf<List<Organization>>(emptyList())
    var projects by mutableStateOf<List<Project>>(emptyList())
    var skills by mutableStateOf<List<Skill>>(emptyList())

    /** Projet choisi explicitement ; sinon le premier disponible. */
    var selectedProjectId by mutableStateOf<String?>(null)
    val selectedProject: Project?
        get() = projects.firstOrNull { it.id == selectedProjectId } ?: projects.firstOrNull()
    val selectedOrg: Organization?
        get() = selectedProject?.let { p -> organizations.firstOrNull { it.id == p.organization_id } }

    /** Organisations repliées dans la barre latérale (toutes dépliées par défaut). */
    var collapsedOrgs by mutableStateOf<Set<String>>(emptySet())
    fun isOrgExpanded(id: String) = id !in collapsedOrgs
    fun toggleOrg(id: String) { collapsedOrgs = if (id in collapsedOrgs) collapsedOrgs - id else collapsedOrgs + id }

    var view by mutableStateOf(ProjectView.Kanban)
    var openTaskId by mutableStateOf<String?>(null)
    var newTaskForStatusId by mutableStateOf<String?>(null)
    var orgFormMode by mutableStateOf<OrgFormMode?>(null)
    var projectFormMode by mutableStateOf<ProjectFormMode?>(null)
    var statusFormMode by mutableStateOf<StatusFormMode?>(null)
    var confirmDeleteOrg by mutableStateOf<Organization?>(null)
    var confirmDeleteProject by mutableStateOf<Project?>(null)
    var confirmDeleteStatus by mutableStateOf<ProjectStatus?>(null)
    var orgsManagerOpen by mutableStateOf(false)
    val dragState = KanbanDragState()

    fun newProject(orgId: String? = selectedOrg?.id ?: organizations.firstOrNull()?.id) {
        if (orgId != null) projectFormMode = ProjectFormMode.New(orgId)
    }

    fun createOrganization(name: String, color: String, icon: String) {
        scope.launch { repos.organizations.create(name = name, color = color, icon = icon, orderIndex = organizations.size.toLong()) }
    }

    fun saveOrganization(initial: Organization?, name: String, color: String, icon: String) {
        scope.launch {
            if (initial == null) {
                repos.organizations.create(name = name, color = color, icon = icon, orderIndex = organizations.size.toLong())
            } else {
                repos.organizations.update(
                    id = initial.id, name = name, description = initial.description, color = color, icon = icon,
                    orderIndex = initial.order_index,
                )
            }
        }
        orgFormMode = null
    }

    fun saveProject(initial: Project?, orgId: String, name: String, color: String, icon: String, description: String, objectives: String, skillId: String?) {
        scope.launch {
            if (initial == null) {
                val newId = repos.projects.create(
                    organizationId = orgId, name = name, color = color, icon = icon,
                    description = description.ifBlank { null }, objectives = objectives.ifBlank { null },
                    orderIndex = projects.count { it.organization_id == orgId }.toLong(),
                )
                if (skillId != null) repos.skills.tagProject(newId, skillId)
                selectedProjectId = newId
            } else {
                repos.projects.update(
                    id = initial.id, organizationId = orgId, objectiveId = initial.objective_id, name = name,
                    description = description.ifBlank { null }, objectives = objectives.ifBlank { null },
                    color = color, icon = icon, orderIndex = initial.order_index,
                )
                if (skillId != initial.skill_id) repos.skills.tagProject(initial.id, skillId)
            }
        }
        projectFormMode = null
    }

    fun deleteOrganization(org: Organization) {
        val inOrg = projects.filter { it.organization_id == org.id }
        scope.launch {
            inOrg.forEach { repos.projects.softDelete(it.id) }
            repos.organizations.softDelete(org.id)
        }
        if (selectedProject?.organization_id == org.id) selectedProjectId = projects.firstOrNull { it.organization_id != org.id }?.id
        confirmDeleteOrg = null
    }

    fun deleteProject(project: Project) {
        scope.launch { repos.projects.softDelete(project.id) }
        if (selectedProjectId == project.id || selectedProject?.id == project.id) selectedProjectId = projects.firstOrNull { it.id != project.id }?.id
        confirmDeleteProject = null
    }

    fun saveStatus(projectId: String, initial: ProjectStatus?, existingCount: Int, name: String, color: String, isTerminal: Boolean) {
        scope.launch {
            if (initial == null) {
                repos.projectStatus.create(projectId = projectId, name = name, color = color, orderIndex = existingCount.toLong(), isTerminal = isTerminal)
            } else {
                repos.projectStatus.update(id = initial.id, name = name, color = color, orderIndex = initial.order_index, isTerminal = isTerminal)
            }
        }
        statusFormMode = null
    }

    fun deleteStatus(status: ProjectStatus) {
        scope.launch { repos.projectStatus.softDelete(status.id) }
        confirmDeleteStatus = null
    }

    fun createTask(projectId: String, statusId: String, title: String) {
        scope.launch { repos.tasks.create(parentKind = ParentKind.Project, parentId = projectId, statusId = statusId, title = title) }
        newTaskForStatusId = null
    }

    fun updateTask(task: Task, title: String, description: String, statusId: String?, priority: String?, dueDate: String) {
        scope.launch {
            repos.tasks.update(
                id = task.id, parentKind = ParentKind.Project, parentId = task.parent_id, statusId = statusId,
                title = title, description = description.ifBlank { null }, status = task.status, priority = priority,
                effort = task.effort, dueDate = dueDate.ifBlank { null }, estimatedMinutes = task.estimated_minutes,
                realMinutes = task.real_minutes, recurrence = task.recurrence, pomodoroPoolDate = task.pomodoro_pool_date,
                orderIndex = task.order_index, completedAt = task.completed_at,
            )
        }
    }

    fun deleteTask(task: Task) {
        scope.launch { repos.tasks.softDelete(task.id) }
        openTaskId = null
    }

    fun toggleDone(task: Task) { scope.launch { repos.tasks.toggleDone(task) } }

    fun sendTaskToPomodoro(task: Task) {
        scope.launch {
            val created = repos.pomodoro.addFromSource(TODAY_ISO, "project_task", task.id)
            if (created != null) repos.tasks.setPomodoroPool(task.id, TODAY_ISO)
        }
    }
}

@Composable
internal fun rememberProjectsModel(repos: TeddRepositories): ProjectsModel {
    val scope = rememberCoroutineScope()
    val model = remember(repos) { ProjectsModel(repos, scope) }
    model.organizations = repos.organizations.observeAll().collectAsState(emptyList()).value
    model.projects = repos.projects.observeAll().collectAsState(emptyList()).value
    model.skills = repos.skills.observeAll().collectAsState(emptyList()).value
    return model
}
